import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { StyleProp, TextStyle } from 'react-native';
import { MBViewProps } from '@shared/components/MBView';
import { validateUrl } from '@shared/util/validate/url';
import { TruncateMiddle } from './TruncateMiddle';

interface Props extends MBViewProps {
  href: string;
  showProtocol?: boolean;
  preserve?: number;
  textStyle?: StyleProp<TextStyle>;
}

export function TruncateLink({
  href,
  showProtocol = false,
  preserve = 8,
  textStyle,
  ...otherProps
}: Props) {
  if (validateUrl(href)) {
    return (
      <TruncateUrl
        href={href}
        showProtocol={showProtocol}
        preserve={preserve}
        textStyle={textStyle}
        {...otherProps}
      />
    );
  } else {
    return <TruncateHref href={href} textStyle={textStyle} {...otherProps} />;
  }
}

function TruncateUrl({
  href,
  showProtocol = false,
  preserve = 8,
  textStyle,
  ...otherProps
}: Props) {
  const url = useMemo(() => new URL(href), [href]);

  // truncate in the middle, preserving last N characters of pathname
  const { origin, hostname, pathname } = url;
  const host = showProtocol ? origin : hostname;
  const path = pathname.replace(/^\//, '');
  const pathEnd = path.slice(-preserve);
  const pathBeginning = path.slice(0, -preserve);
  const truncatedString = [host, !isEmpty(path) && '/', pathBeginning].filter(Boolean).join('');

  return (
    <TruncateMiddle
      beginning={truncatedString}
      end={pathEnd}
      textStyle={textStyle}
      {...otherProps}
    />
  );
}

function TruncateHref({
  href,
  textStyle,
  ...otherProps
}: Omit<Props, 'preserve' | 'showProtocol'>) {
  return <TruncateMiddle beginning={href} textStyle={textStyle} {...otherProps} />;
}
